import { store } from "../../redux/store";
import {
  addOpenOrder,
  addPendingOrder,
  removeOpenOrder,
  removePendingOrder,
  updateOpenOrder,
  updateOpenPositionProfits,
  addOrderHistory,
  removeOrderHistory,
  setSocketStatus,
  updatePendingOrder,
} from "../../redux/features/platformSlice";
import {
  calcSingleProfit,
  isBuyOrder,
  isPendingOrder,
} from "../platform/TradingStore";
import { refreshAccessToken } from "../platform/api";
import {
  isOrderHistoryDeal,
  normalizeDeal,
  normalizeOrder as normalizeApiOrder,
  normalizePosition,
} from "../platform/normalizers";
import {
  getPriceSymbolCandidates,
  normalizePriceSymbol,
} from "../../utils/symbolPriceFilter";

class MarketDataService {
  static instance = null;

  prices = {};
  priceHistory = {};
  maxHistoryLength = 30;

  listeners = {};
  globalListeners = [];
  metricsListeners = [];

  socket = null;
  socketUrl = null;
  token = null;
  receivePrices = true;
  isConnected = false;
  manualDisconnect = false;
  reconnectTimer = null;
  reconnectAttempts = 0;
  chartService = null;

  throttleTimers = {};
  throttleDelay = 100;

  dynamicSymbols = [];
  dynamicSymbolMap = {};

  constructor() {
    if (MarketDataService.instance) return MarketDataService.instance;
    MarketDataService.instance = this;
  }

  static getInstance() {
    if (!MarketDataService.instance) new MarketDataService();
    return MarketDataService.instance;
  }

  connect(url, token, options = {}) {
    if (!url) return;

    const receivePrices = options.prices !== false;
    const forceReconnect = options.force === true;
    if (forceReconnect) {
      this.cleanupSocket();
    } else if (
      this.socketUrl !== url ||
      this.token !== token ||
      this.receivePrices !== receivePrices
    ) {
      this.cleanupSocket();
    }

    this.socketUrl = url;
    this.token = token;
    this.receivePrices = receivePrices;
    this.manualDisconnect = false;

    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    const finalUrl = this.buildSocketUrl(url, token, receivePrices);
    const socket = new WebSocket(finalUrl);
    this.socket = socket;
    store.dispatch(setSocketStatus("connecting"));

    socket.onopen = () => {
      if (this.socket !== socket) return;
      this.isConnected = true;
      this.reconnectAttempts = 0;
      store.dispatch(setSocketStatus("connected"));
    };
    socket.onmessage = (event) => this.handleMessage(event);
    socket.onerror = (error) => {
      console.error("[MarketDataService] socket error:", error);
      if (this.socket === socket) store.dispatch(setSocketStatus("error"));
    };
    socket.onclose = (event) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.isConnected = false;
      store.dispatch(
        setSocketStatus(this.manualDisconnect ? "disconnected" : "reconnecting"),
      );
      if (!this.manualDisconnect) {
        if (event?.code === 1008 && this.token) {
          refreshAccessToken()
            .then((token) => {
              if (this.manualDisconnect) return;
              this.connect(this.socketUrl, token, { prices: this.receivePrices });
            })
            .catch(() => this.scheduleReconnect());
          return;
        }
        this.scheduleReconnect();
      }
    };
  }

  buildSocketUrl(url, token, receivePrices = true) {
    try {
      const socketUrl = new URL(url);
      if (token) socketUrl.searchParams.set("token", token);
      else socketUrl.searchParams.delete("token");
      socketUrl.searchParams.set("prices", String(receivePrices));
      return socketUrl.toString();
    } catch {
      const params = new URLSearchParams();
      if (token) params.set("token", token);
      params.set("prices", String(receivePrices));
      return `${url}${url.includes("?") ? "&" : "?"}${params.toString()}`;
    }
  }

  scheduleReconnect() {
    if (this.manualDisconnect || this.reconnectTimer || !this.socketUrl) return;
    const delay = Math.min(30000, 1000 * (2 ** this.reconnectAttempts));
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(this.socketUrl, this.token, { prices: this.receivePrices });
    }, delay);
  }

  handleMessage(event) {
    try {
      const message = JSON.parse(event.data);
      const eventName = message?.event;

      switch (eventName) {
        case "price.snapshot":
          this.setInitialPrices(
            message?.data?.items || message?.data || message?.prices,
          );
          break;
        case "price.tick":
          this.handlePriceTick(message);
          break;
        case "order.pending.created":
          this.handlePendingOrderCreated(message);
          break;
        case "order.created":
          this.handleOrderCreated(message);
          break;
        case "order.partially_filled":
        case "order.updated":
          this.handleOrderUpdated(message);
          break;
        case "order.filled":
        case "order.cancelled":
        case "order.expired":
        case "order.removed":
        case "order.rejected":
          this.handleOrderCancelled(message);
          break;
        case "position.opened":
          this.handlePositionOpened(message);
          break;
        case "position.closed":
          this.handlePositionClosed(message);
          break;
        case "position.updated":
          this.handlePositionUpdated(message);
          break;
        case "deal.created":
        case "deal.updated":
          this.handleDealUpsert(message);
          break;
        case "deal.removed":
          this.handleDealRemoved(message);
          break;
        case "socket.connected":
        case "socket.authenticated":
        case "price.feed.started":
        case "price.feed.disabled":
        case "user.feed.started":
        case "socket.ping":
          break;
        default:
          break;
      }

    } catch (error) {
      console.error("[MarketDataService] message error:", error);
    }
  }

  getPayload(message) {
    return message?.data && typeof message.data === "object"
      ? message.data
      : message;
  }

  handlePriceTick(message) {
    const tick = this.getPayload(message);
    const rawSymbol = tick.symbol;
    const symbol = normalizePriceSymbol(rawSymbol);
    if (!symbol) return;

    const bid = Number(tick.bid);
    const ask = Number(tick.ask);
    if (!Number.isFinite(bid) || !Number.isFinite(ask)) return;

    const previous = this.prices[symbol] || {};
    const meta = this.getSymbolMeta(symbol);
    const digits = Number(tick.digits ?? previous.digits ?? meta?.digits ?? 5);
    const spread = Math.abs(ask - bid);
    let timestamp = Number(
      tick.time_msc ?? tick.timeMsc ?? tick.timestamp ?? tick.time ?? Date.now(),
    );
    if (timestamp < 100000000000) timestamp *= 1000;
    if (!Number.isFinite(timestamp)) timestamp = Date.now();

    const price = {
      ...previous,
      symbol: rawSymbol,
      bid,
      ask,
      spread,
      high: Number.isFinite(Number(tick.high))
        ? Number(tick.high)
        : previous.high
          ? Math.max(previous.high, bid)
          : bid,
      low: Number.isFinite(Number(tick.low))
        ? Number(tick.low)
        : previous.low
          ? Math.min(previous.low, bid)
          : bid,
      time: timestamp,
      timestamp,
      digits,
    };

    this.prices[symbol] = price;
    this.addPriceToHistory(symbol, bid);
    this.schedulePositionProfitUpdate(symbol);

    if (getPriceSymbolCandidates(this.chartService?.symbol).includes(symbol)) {
      this.chartService.updateTickPrice({ bid, ask, timestamp });
    }

    this.notifySubscribers(symbol);
    this.notifyGlobalSubscribers();
    this.notifyMetricsSubscribers();
  }

  normalizeOrder(message) {
    return normalizeApiOrder(this.getPayload(message), message);
  }

  handlePendingOrderCreated(message) {
    const order = this.normalizeOrder(message);
    if (!order.ticket || !order.symbol) return;
    store.dispatch(addPendingOrder(order));
    this.notifyMetricsSubscribers();
  }

  schedulePositionProfitUpdate(symbol) {
    const timerKey = `position-profit:${symbol}`;
    if (this.throttleTimers[timerKey]) return;

    this.throttleTimers[timerKey] = setTimeout(() => {
      delete this.throttleTimers[timerKey];
      this.updatePositionProfits(symbol, this.prices[symbol]);
    }, this.throttleDelay);
  }

  updatePositionProfits(symbol, price) {
    const normalizedSymbol = normalizePriceSymbol(symbol);
    const bid = Number(price?.bid);
    const ask = Number(price?.ask);
    if (!normalizedSymbol || !Number.isFinite(bid) || !Number.isFinite(ask)) return;

    const positions = store.getState()?.platform?.positions || [];
    const updates = positions
      .filter(position =>
        normalizePriceSymbol(position?.symbol) === normalizedSymbol,
      )
      .map(position => {
        const currentPrice = isBuyOrder(position) ? bid : ask;
        const profit = calcSingleProfit(position, currentPrice);
        if (!Number.isFinite(profit)) return null;

        return {
          ...position,
          price_current: currentPrice,
          priceCurrent: currentPrice,
          current_price: currentPrice,
          currentPrice,
          profit,
        };
      })
      .filter(Boolean);

    if (updates.length) store.dispatch(updateOpenPositionProfits(updates));
  }

  handleOrderCreated(message) {
    const order = this.normalizeOrder(message);
    if (!order.ticket || !order.symbol) return;

    if (isPendingOrder(order)) store.dispatch(addPendingOrder(order));
    this.notifyMetricsSubscribers();
  }

  handlePositionOpened(message) {
    const position = normalizePosition(this.getPayload(message), message);
    if (!position.ticket || !position.symbol) return;
    store.dispatch(addOpenOrder(position));
    this.notifyMetricsSubscribers();
  }

  handlePositionClosed(message) {
    const position = normalizePosition(this.getPayload(message), message);
    store.dispatch(removeOpenOrder(position));
    this.notifyMetricsSubscribers();
  }

  handlePositionUpdated(message) {
    const position = normalizePosition(this.getPayload(message), message);
    // console.log("🚀 ~ MarketDataService ~ handlePositionUpdated ~ data:", data)
    store.dispatch(updateOpenOrder(position));
    this.notifyMetricsSubscribers();
  }

  handleOrderUpdated(message) {
    const order = this.normalizeOrder(message);
    if (isPendingOrder(order)) store.dispatch(updatePendingOrder(order));
    this.notifyMetricsSubscribers();
  }

  handleOrderCancelled(message) {
    const order = this.normalizeOrder(message);
    store.dispatch(removePendingOrder(order));
    this.notifyMetricsSubscribers();
  }

  handleDealUpsert(message) {
    const payload = this.getPayload(message);
    const incomingId = payload?.deal ?? payload?.ticket ?? payload?.id;
    const existing = incomingId
      ? (store.getState()?.platform?.orderHistory || []).find(item =>
        String(item?.deal ?? item?.ticket ?? item?.id) === String(incomingId),
      )
      : null;
    const deal = normalizeDeal({ ...(existing || {}), ...payload });
    if (!deal.deal) return;
    if (!isOrderHistoryDeal(deal)) {
      if (existing) store.dispatch(removeOrderHistory(deal));
      return;
    }
    store.dispatch(addOrderHistory(deal));
    this.notifyMetricsSubscribers();
  }

  handleDealRemoved(message) {
    const deal = normalizeDeal(this.getPayload(message), message);
    store.dispatch(removeOrderHistory(deal));
    this.notifyMetricsSubscribers();
  }

  cleanupSocket() {
    if (!this.socket) return;
    this.socket.onopen = null;
    this.socket.onmessage = null;
    this.socket.onerror = null;
    this.socket.onclose = null;
    if (
      this.socket.readyState === WebSocket.OPEN ||
      this.socket.readyState === WebSocket.CONNECTING
    ) {
      this.socket.close();
    }
    this.socket = null;
    this.isConnected = false;
  }

  disconnect() {
    this.manualDisconnect = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    Object.values(this.throttleTimers).forEach(clearTimeout);
    this.throttleTimers = {};
    this.cleanupSocket();
    store.dispatch(setSocketStatus("disconnected"));
  }

  subscribe(symbol, callback) {
    const candidates = Array.from(
      new Set(getPriceSymbolCandidates(symbol).map(normalizePriceSymbol)),
    ).filter(Boolean);
    const emit = () => callback(this.getPrice(symbol));
    candidates.forEach((candidate) => {
      if (!this.listeners[candidate]) this.listeners[candidate] = [];
      this.listeners[candidate].push(emit);
    });

    const price = this.getPrice(symbol);
    if (price) setTimeout(() => callback(price), 0);

    return () =>
      candidates.forEach((candidate) => this.unsubscribe(candidate, emit));
  }

  unsubscribe(symbol, callback) {
    const key = normalizePriceSymbol(symbol);
    if (!this.listeners[key]) return;
    this.listeners[key] = this.listeners[key].filter((cb) => cb !== callback);
    if (!this.listeners[key].length) delete this.listeners[key];
  }

  subscribeAll(callback) {
    this.globalListeners.push(callback);
    if (Object.keys(this.prices).length) {
      queueMicrotask(() => callback({ ...this.prices }));
    }
    return () => {
      this.globalListeners = this.globalListeners.filter(
        (cb) => cb !== callback,
      );
    };
  }

  subscribeMetrics(callback) {
    this.metricsListeners.push(callback);
    return () => {
      this.metricsListeners = this.metricsListeners.filter(
        (cb) => cb !== callback,
      );
    };
  }

  notifySubscribers(symbol) {
    const subscribers = this.listeners[symbol];
    if (!subscribers?.length || this.throttleTimers[symbol]) return;
    this.throttleTimers[symbol] = setTimeout(() => {
      const price = this.prices[symbol];
      subscribers.forEach((callback) => {
        try {
          callback(price);
        } catch {}
      });
      delete this.throttleTimers[symbol];
    }, this.throttleDelay);
  }

  notifyGlobalSubscribers() {
    if (!this.globalListeners.length || this.throttleTimers.global) return;
    this.throttleTimers.global = setTimeout(() => {
      const prices = { ...this.prices };
      this.globalListeners.forEach((callback) => {
        try {
          callback(prices);
        } catch {}
      });
      delete this.throttleTimers.global;
    }, this.throttleDelay);
  }

  notifyMetricsSubscribers() {
    if (!this.metricsListeners.length || this.throttleTimers.metrics) return;
    this.throttleTimers.metrics = setTimeout(() => {
      this.metricsListeners.forEach((callback) => {
        try {
          callback();
        } catch {}
      });
      delete this.throttleTimers.metrics;
    }, this.throttleDelay);
  }

  setDynamicSymbols(symbols) {
    if (!Array.isArray(symbols)) return;
    this.dynamicSymbols = symbols;
    this.dynamicSymbolMap = {};
    symbols.forEach((symbol) => {
      getPriceSymbolCandidates(symbol?.symbol).forEach((candidate) => {
        const key = normalizePriceSymbol(candidate);
        if (key) this.dynamicSymbolMap[key] = symbol;
      });
    });
  }

  setInitialPrices(prices) {
    const list = Array.isArray(prices) ? prices : prices?.data;
    if (!Array.isArray(list)) return;
    list.forEach((price) => {
      if (price?.bid !== undefined && price?.ask !== undefined) {
        this.handlePriceTick({ data: price });
      }
    });
  }

  setChartService(service) {
    this.chartService = service;
  }

  getPrice(symbol) {
    return this.prices[normalizePriceSymbol(symbol)] || null;
  }

  getAllPrices() {
    return { ...this.prices };
  }

  getPriceHistory(symbol) {
    return this.priceHistory[normalizePriceSymbol(symbol)] || [];
  }

  getSymbolsMeta() {
    return this.dynamicSymbols;
  }

  getSymbolMeta(symbol) {
    return this.dynamicSymbolMap[normalizePriceSymbol(symbol)] || null;
  }

  getStaticSymbol(symbol) {
    return this.getSymbolMeta(symbol);
  }

  addPriceToHistory(symbol, price) {
    const key = normalizePriceSymbol(symbol);
    if (!key || !Number.isFinite(price)) return;
    if (!this.priceHistory[key]) this.priceHistory[key] = [];
    this.priceHistory[key].push(price);
    if (this.priceHistory[key].length > this.maxHistoryLength) {
      this.priceHistory[key].shift();
    }
  }

  getPriceChange(symbol) {
    const price = this.getPrice(symbol);
    if (!price?.open || !price?.bid) {
      return { change: 0, percentage: 0, isPositive: true };
    }
    const change = price.bid - price.open;
    return {
      change,
      percentage: (change / price.open) * 100,
      isPositive: change >= 0,
    };
  }

  getSpreadFromPrice(price, fallbackSpread) {
    const bid = Number(price?.bid);
    const ask = Number(price?.ask);
    if (Number.isFinite(bid) && Number.isFinite(ask)) {
      return Math.abs(ask - bid);
    }

    const fallback = Number(fallbackSpread);
    return Number.isFinite(fallback) ? Math.abs(fallback) : null;
  }

  formatSpreadInPips(spreadValue, symbolDigits, price) {
    const spreadFromPrice = this.getSpreadFromPrice(price, spreadValue);
    if (spreadFromPrice === null) return "-";
    const digits = Number(symbolDigits);
    const safeDigits = Number.isFinite(digits)
      ? digits
      : Number(price?.digits ?? 5);
    const multiplier =
      safeDigits === 3 || safeDigits === 5
        ? Math.pow(10, safeDigits - 1)
        : Math.pow(10, safeDigits);
    const pips = spreadFromPrice * multiplier;
    return pips.toFixed(1);
  }

  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      isDemo: false,
      symbolCount: Object.keys(this.prices).length,
    };
  }
}

const instance = new MarketDataService();
export default instance;
